import React, { useState, useEffect, useMemo } from 'react';
import { useAlert } from '../../providers/AlertProvider';
import { useSupabase } from '../../providers/SupabaseProvider';
import { getProductsWithStock, createOrder, getOrders, updateOrderStatus, getProductPalletLocations } from '../../api/orders';
import OrderDetailsModal from './OrderDetailsModal';
import logger from "../../utils/logger";


/**
 * OrdersManagement.jsx
 * 
 * Component for managing warehouse orders:
 * - Place new orders (select products with quantities)
 * - View existing orders
 * - Filter products by name, code, or batch
 * - Customer-specific access controls
 */
export default function OrdersManagement({
    theme = "light",
    customerName = "",
    customers = [],
    warehouseCustomers = [],
    warehouseLanes = {},
    warehouseProducts = [],
    selectedCustomerId = null, // Add this prop from warehouse management
    triggerRefresh,
    isCustomer = false,
    profile = null,
    isAdmin = false,
    isWarehouse = false,
    isManagement = false,
    warehouseData = [] // New prop to pass warehouse data for pick note generation
}) {
    const { showAlert } = useAlert();
    const supabase = useSupabase();

    // Helper function to get customer name - must be declared early to avoid temporal dead zone
    const getCustomerName = (customerId) => {
        // First try warehouseCustomers, then fall back to customers array
        const customer = warehouseCustomers.find(c => c.id === parseInt(customerId)) || 
                         customers.find(c => c.id === parseInt(customerId));
        return customer ? customer.company || customer.name : 'Unknown Customer';
    };
    
    // Track changes to warehouseProducts
    useEffect(() => {
        logger.log('🔄 OrdersManagement - warehouseProducts changed:', {
            type: typeof warehouseProducts,
            isArray: Array.isArray(warehouseProducts),
            length: warehouseProducts?.length,
            isEmpty: !warehouseProducts || (Array.isArray(warehouseProducts) && warehouseProducts.length === 0),
            firstItem: warehouseProducts?.[0]
        });
    }, [warehouseProducts]);

    // Track changes to selectedCustomerId
    useEffect(() => {
        logger.log('🔄 OrdersManagement - selectedCustomerId changed:', {
            selectedCustomerId,
            type: typeof selectedCustomerId,
            customerName: selectedCustomerId ? getCustomerName(selectedCustomerId) : 'No customer selected'
        });
        
        // Clear initial loading state after a short delay to allow for async customer loading
        const timer = setTimeout(() => {
            setIsInitialLoad(false);
        }, 1500); // Give 1.5 seconds for customer data to load
        
        return () => clearTimeout(timer);
    }, [selectedCustomerId, warehouseCustomers, customers]);
    
    // State management
    const [activeTab, setActiveTab] = useState(isManagement || isCustomer || isWarehouse ? 'place-order' : 'view-orders');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true); // Track if we're in initial loading
    
    // Order placement state
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProducts, setSelectedProducts] = useState([]); // {product_id, pallet_id, quantity, max_quantity}
    const [orderNotes, setOrderNotes] = useState('');
    
    // New state for two-step product selection
    const [selectedProductForPallets, setSelectedProductForPallets] = useState(null); // Currently selected product to show pallets
    const [productPallets, setProductPallets] = useState([]); // Pallets for the selected product
    const [isLoadingPallets, setIsLoadingPallets] = useState(false);
    const [palletFilter, setPalletFilter] = useState(''); // Filter for pallet search

    // Custom sorting function for pallet codes (W1A001A format)
    const sortPalletCode = (a, b) => {
        const aCode = a.pallet_code || '';
        const bCode = b.pallet_code || '';
        
        // Handle empty codes
        if (!aCode && !bCode) return 0;
        if (!aCode) return 1;
        if (!bCode) return -1;
        
        return aCode.localeCompare(bCode, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
        });
    };

    // Filtered and sorted products using useMemo for better performance
    const filteredProducts = useMemo(() => {
        // Convert warehouseProducts to array if it's an object
        let productsArray = [];
        if (Array.isArray(warehouseProducts)) {
            productsArray = warehouseProducts;
        } else if (warehouseProducts && typeof warehouseProducts === 'object') {
            // Convert object with numbered keys to array
            productsArray = Object.values(warehouseProducts);
        }
        
        logger.log('🔍 Filtered Products Debug:');
        logger.log('- Original warehouseProducts:', warehouseProducts);
        logger.log('- Converted to array:', productsArray);
        logger.log('- Array length:', productsArray.length);
        
        let filtered = productsArray || [];
        
        // Apply search filter only (products are already filtered by customer)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product => 
                product.product_name?.toLowerCase().includes(term) ||
                product.product_code?.toLowerCase().includes(term) ||
                product.batch_no?.toLowerCase().includes(term)
            );
        }
        
        // Sort by product code for consistent display
        return filtered.sort((a, b) => {
            const aCode = a.product_code || '';
            const bCode = b.product_code || '';
            return aCode.localeCompare(bCode);
        });
    }, [warehouseProducts, searchTerm]);
    
    // Filter pallets based on search term
    const filteredPallets = useMemo(() => {
        if (!palletFilter.trim()) {
            return productPallets;
        }
        
        const term = palletFilter.toLowerCase();
        return productPallets.filter(pallet => 
            pallet.pallet_code?.toLowerCase().includes(term) ||
            pallet.lane_id?.toLowerCase().includes(term) ||
            pallet.pallet_type?.toLowerCase().includes(term) ||
            pallet.status?.toLowerCase().includes(term)
        );
    }, [productPallets, palletFilter]);
    
    // Delivery information state
    const [deliveryInfo, setDeliveryInfo] = useState({
        delivery_company: '',
        delivery_address: '',
        delivery_postcode: '',
        delivery_date: '',
        delivery_time: '',
        collection_date: '',
        collection_courier: ''
    });
    
    // Orders list state
    const [orders, setOrders] = useState([]);
    const [realtimeOrder, setRealtimeOrder] = useState(null);
    const [realtimeStatus, setRealtimeStatus] = useState('disconnected');
    
    // Order details modal state
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
    
    // Theme colors - matching warehouse styling
    const isDark = theme === "dark";
    const color = {
        bg: isDark ? "#080808" : "#fff",
        panel: isDark ? "#333" : "#fff",
        fg: isDark ? "#fff" : "#000",
        border: isDark ? "#fff" : "#000",
        headerBg: isDark ? "#2b2b2b" : "#eee",
        highlight: "rgba(26, 221, 247, 0.6)",
        primary: isDark ? "#0d6efd" : "#007bff",
        success: isDark ? "#198754" : "#28a745",
        warning: "#ffc107",
        danger: "#dc3545",
    };

    // Initialize customer selection for customers
    // Load orders when component mounts or tab changes
    useEffect(() => {
        if (activeTab === 'view-orders') {
            loadOrders();
        }
    }, [activeTab]);

    const loadOrders = async (showLoading = true) => {
        if (showLoading) {
            setIsLoading(true);
        }
        try {
            const customerId = isCustomer && profile?.customer_id ? profile.customer_id : null;
            const response = await getOrders(customerId);
            
            if (response.success) {
                setOrders(response.data.orders || []);
            } else {
                showAlert({
                    message: response.message || 'Failed to load orders',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error loading orders:', error);
            showAlert({
                message: 'Error loading orders',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            if (showLoading) {
                setIsLoading(false);
            }
        }
    };

    const handleOrderUpdate = async (updatedOrder) => {
        if (updatedOrder?.id) {
            setOrders(currentOrders => currentOrders.map(currentOrder =>
                currentOrder.id === updatedOrder.id
                    ? { ...currentOrder, ...updatedOrder }
                    : currentOrder
            ));
        }

        await loadOrders(false);
    };

    useEffect(() => {
        if (!supabase) {
            setRealtimeStatus('unavailable');
            return;
        }

        setRealtimeStatus('connecting');

        const channel = supabase
            .channel(`warehouse-orders-${Date.now()}`)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'all_orders' },
                (payload) => {
                    const changedOrder = payload.new?.id ? payload.new : payload.old;
                    if (!changedOrder?.id) return;

                    const customerId = profile?.customer_id;
                    const canDisplayOrder = !isCustomer
                        || !customerId
                        || Number(changedOrder.customer_id) === Number(customerId);

                    setOrders(currentOrders => {
                        if (payload.eventType === 'DELETE' || !canDisplayOrder) {
                            return currentOrders.filter(order => order.id !== changedOrder.id);
                        }

                        const existingIndex = currentOrders.findIndex(order => order.id === changedOrder.id);
                        if (existingIndex === -1) {
                            return [changedOrder, ...currentOrders];
                        }

                        return currentOrders.map(order =>
                            order.id === changedOrder.id
                                ? { ...order, ...changedOrder }
                                : order
                        );
                    });

                    if (payload.eventType !== 'DELETE' && canDisplayOrder) {
                        setRealtimeOrder(changedOrder);
                    }
                }
            )
            .subscribe((status) => {
                setRealtimeStatus(status.toLowerCase());
                if (status === 'SUBSCRIBED') {
                    logger.info('Subscribed to warehouse order realtime updates');
                } else if (status === 'CHANNEL_ERROR') {
                    logger.error('Warehouse order realtime subscription failed');
                }
            });

        return () => {
            setRealtimeStatus('disconnected');
            supabase.removeChannel(channel);
        };
    }, [supabase, isCustomer, profile?.customer_id]);

    // Load pallets for a selected product using the new API
    const loadProductPallets = async (product) => {
        if (!product) {
            logger.warn('⚠️ Cannot load pallets: missing product');
            return;
        }

        setIsLoadingPallets(true);
        setSelectedProductForPallets(product);
        
        try {
            // Instead of using just the warehouse ID, pass identifying fields
            // The backend can use these to match with the actual database product
            const response = await getProductPalletLocations({
                warehouse_id: product.id,
                product_code: product.product_code,
                product_name: product.product_name,
                batch_no: product.batch_no
            }, selectedCustomerId);
            
            if (response.success) {
                const palletLocations = response.data || [];
                
                // Transform the data to match the expected format
                const transformedPallets = palletLocations.map(pallet => ({
                    pallet_id: pallet.pallet_id,
                    pallet_code: pallet.pallet_code,
                    pallet_type: pallet.pallet_type,
                    lane_id: pallet.lane_id,
                    stock: pallet.stock,
                    batch_no: pallet.batch_no,
                    status: pallet.status,
                    created_at: pallet.created_at,
                    qr_link: pallet.qr_link,
                    pending_order: pallet.pending_order || false,
                    ordered_quantity: pallet.ordered_quantity || [],
                    // Additional fields for order management
                    product_id: product.id,
                    product_name: product.product_name,
                    product_code: product.product_code
                }));
                
                // Sort by lane_id then pallet_code
                const sortedPallets = transformedPallets.sort((a, b) => {
                    const laneCompare = (a.lane_id || '').localeCompare(b.lane_id || '');
                    if (laneCompare !== 0) return laneCompare;
                    return sortPalletCode(a, b);
                });
                
                setProductPallets(sortedPallets);
            } else {
                throw new Error(response.message || 'Failed to load pallet locations');
            }
            
        } catch (error) {
            logger.error('❌ Error loading pallets for product:', error);
            showAlert({
                message: 'Failed to load pallet locations for this product',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            setProductPallets([]);
        } finally {
            setIsLoadingPallets(false);
        }
    };

    // Go back to product selection
    const backToProductSelection = () => {
        setSelectedProductForPallets(null);
        setProductPallets([]);
    };

    // Helper function to find pallet info from the fetched pallet locations
    const findPalletInfo = (palletId) => {
        return productPallets.find(pallet => pallet.pallet_id === palletId) || null;
    };

    const toggleProductSelection = (product, palletId) => {
        setSelectedProducts(prev => {
            const key = `${product.id}-${palletId}`;
            const existingIndex = prev.findIndex(p => `${p.product_id}-${p.pallet_id}` === key);
            
            if (existingIndex >= 0) {
                // Remove if already selected
                return prev.filter((_, index) => index !== existingIndex);
            } else {
                // Find pallet info from the API response data
                const pallet = findPalletInfo(palletId);
                let defaultQuantity = 1;
                
                // If it's a standard pallet, set quantity to full amount available on that pallet
                if (pallet && pallet.pallet_type === 'standard') {
                    defaultQuantity = pallet.stock || 1; // Use the pallet's stock, fallback to 1
                }
                
                // Add with appropriate default quantity
                return [...prev, {
                    product_id: product.id,
                    pallet_id: palletId,
                    product_code: product.product_code,
                    product_name: product.product_name,
                    batch_no: product.batch_no,
                    quantity: defaultQuantity,
                    max_quantity: pallet ? pallet.stock : 1, // Use pallet's stock as max
                    pallet_code: pallet ? pallet.pallet_code : 'Unknown'
                }];
            }
        });
    };

    const updateSelectedQuantity = (productId, palletId, newQuantity) => {
        setSelectedProducts(prev => 
            prev.map(item => 
                item.product_id === productId && item.pallet_id === palletId
                    ? { ...item, quantity: Math.min(Math.max(1, parseInt(newQuantity) || 1), item.max_quantity) }
                    : item
            )
        );
    };

    const removeSelectedProduct = (productId, palletId) => {
        setSelectedProducts(prev => 
            prev.filter(item => !(item.product_id === productId && item.pallet_id === palletId))
        );
    };

    // UK postcode validation
    const isValidPostcode = (postcode) => {
        if (!postcode || !postcode.trim()) return false;
        // UK postcode regex pattern
        const ukPostcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
        return ukPostcodeRegex.test(postcode.trim());
    };

    const placeOrder = async () => {
        if (!selectedCustomerId) {
            showAlert({
                message: 'Please select a customer',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        if (selectedProducts.length === 0) {
            showAlert({
                message: 'Please select at least one product',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        // Validate delivery information
        const errors = [];
        
        if (!deliveryInfo.delivery_company.trim()) {
            errors.push('Delivery Name is required');
        }
        
        if (!deliveryInfo.delivery_date) {
            errors.push('Delivery Date is required');
        }
        
        if (!deliveryInfo.delivery_postcode.trim()) {
            errors.push('Delivery Postcode is required');
        } else if (!isValidPostcode(deliveryInfo.delivery_postcode)) {
            errors.push('Please enter a valid UK postcode format');
        }
        
        if (!deliveryInfo.delivery_address.trim()) {
            errors.push('Delivery Address is required');
        }

        if (errors.length > 0) {
            showAlert({
                message: `Please fix the following errors:\n\n${errors.join('\n')}`,
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        setIsLoading(true);
        try {
            const orderData = {
                customer_id: parseInt(selectedCustomerId),
                order_items: selectedProducts.map(item => ({
                    product_id: item.product_id,
                    pallet_id: item.pallet_id,
                    requested_quantity: item.quantity,
                    available_quantity: item.max_quantity
                })),
                notes: orderNotes.trim() || null,
                delivery_info: deliveryInfo
            };

            const response = await createOrder(orderData);

            if (response.success) {
                showAlert({
                    message: `Order ${response.data.order_number} created successfully!`,
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                
                // Reset form
                setSelectedProducts([]);
                setOrderNotes('');
                setSearchTerm('');
                setDeliveryInfo({
                    delivery_company: '',
                    delivery_address: '',
                    delivery_postcode: '',
                    delivery_date: '',
                    delivery_time: '',
                    collection_date: '',
                    collection_courier: ''
                });
                
                // Trigger warehouse data refresh to update pending_order visual indicators
                if (triggerRefresh) {
                    logger.log('🔄 Triggering warehouse data refresh after order placement');
                    triggerRefresh();
                } else {
                    logger.warn('⚠️ triggerRefresh function not available - visual indicators may not update immediately');
                }
            } else {
                showAlert({
                    message: response.message || 'Failed to create order',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error creating order:', error);
            
            // Check if it's a session expiration error
            if (error.message.includes('session has expired')) {
                showAlert({
                    message: 'Your session has expired. Please log in again.',
                    type: 'warning',
                    buttons: [
                        { 
                            label: 'Login Again', 
                            action: () => {
                                window.location.href = '/login';
                            }
                        }
                    ]
                });
            } else {
                showAlert({
                    message: error.message || 'Error creating order',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const isProductSelected = (productId, palletId) => {
        return selectedProducts.some(p => p.product_id === productId && p.pallet_id === palletId);
    };



    const getSelectedCustomerName = () => {
        if (!selectedCustomerId) return null;
        return getCustomerName(selectedCustomerId);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDeliveryInfoChange = (field, value) => {
        setDeliveryInfo(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const openOrderDetails = (orderId) => {
        setSelectedOrderId(orderId);
        setOrderDetailsOpen(true);
    };

    const closeOrderDetails = () => {
        setSelectedOrderId(null);
        setOrderDetailsOpen(false);
    };

    return (
        <div
            style={{
                fontFamily: "sans-serif",
                padding: "1rem",
                backgroundColor: color.bg,
                color: color.fg,
                minHeight: "100%",
            }}
        >
            {/* Tab Navigation */}
            <div style={{
                display: 'flex',
                marginBottom: '2rem',
                borderBottom: `2px solid ${color.border}`,
                backgroundColor: color.panel,
                borderRadius: '8px 8px 0 0',
                padding: '0.75rem 1rem'
            }}>
                {(isManagement || isCustomer || isWarehouse) && (
                <button
                    onClick={() => setActiveTab('place-order')}
                    style={{
                        padding: '0.5rem 0.75rem',
                        marginRight: '0.5rem',
                        backgroundColor: activeTab === 'place-order' ? color.primary : 'transparent',
                        color: activeTab === 'place-order' ? '#fff' : color.fg,
                        border: `2px solid ${color.border}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '1rem',
                        outline: 'none'
                    }}
                >
                    Place Order
                </button>
                )}
                <button
                    onClick={() => setActiveTab('view-orders')}
                    style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: activeTab === 'view-orders' ? color.primary : 'transparent',
                        color: activeTab === 'view-orders' ? '#fff' : color.fg,
                        border: `2px solid ${color.border}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '1rem',
                        outline: 'none'
                    }}
                >
                    View Orders
                </button>
            </div>

            {/* Place Order Tab */}
            {activeTab === 'place-order' && (
                <div style={{ marginTop: "2rem" }}>

                    {/* Product Search and Selection */}
                    {!selectedCustomerId ? (
                        <section
                            style={{
                                marginBottom: "2.5rem",
                                border: `3px solid ${color.border}`,
                                borderRadius: 8,
                                backgroundColor: color.panel,
                                padding: "2rem",
                                textAlign: "center"
                            }}
                        >
                            {isInitialLoad ? (
                                <>
                                    <h3 style={{ margin: "0 0 1rem 0", color: color.fg }}>
                                        Loading Customer Data...
                                    </h3>
                                    <p style={{ margin: "0", color: color.fg, fontSize: "1.1rem" }}>
                                        Please wait while we load your customer information.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3 style={{ margin: "0 0 1rem 0", color: color.fg }}>
                                        Customer Selection Required
                                    </h3>
                                    <p style={{ margin: "0", color: color.fg, fontSize: "1.1rem" }}>
                                        Please select a customer from the warehouse management page to view available products and place orders.
                                    </p>
                                </>
                            )}
                        </section>
                    ) : selectedCustomerId && (
                        <>
                            {/* Selected Customer Display */}
                            <section
                                style={{
                                    marginBottom: "1.5rem",
                                    border: `2px solid ${color.success}`,
                                    borderRadius: 8,
                                    backgroundColor: color.panel,
                                    padding: "1rem"
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                                    <span style={{ 
                                        color: color.success, 
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold'
                                    }}>
                                        ✓
                                    </span>
                                    <div>
                                        <h4 style={{ margin: 0, color: color.fg }}>
                                            Selected Customer: {customerName || getSelectedCustomerName()}
                                        </h4>
                                        <p style={{ margin: '0.25rem 0 0 0', opacity: 0.8, fontSize: '0.9rem' }}>
                                            Customer ID: {selectedCustomerId}
                                        </p>
                                    </div>
                                </div>
                            </section>

                            {/* Product Selection */}
                            <section
                                style={{
                                    marginBottom: "2.5rem",
                                    border: `3px solid ${color.border}`,
                                    borderRadius: 8,
                                    backgroundColor: color.panel,
                                }}
                            >
                                <h3
                                    style={{
                                        margin: "0",
                                        padding: "0.75rem 1rem",
                                        borderBottom: `1px solid ${color.border}`,
                                    }}
                                >
                                    Select Products ({filteredProducts.length} available)
                                </h3>
                                
                                <div style={{ padding: "1rem" }}>
                                    {/* Search Box */}
                                    <div style={{ marginBottom: "1rem" }}>
                                        <input
                                            type="text"
                                            placeholder="Search by product name, code, or batch..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{
                                                padding: ".5rem .75rem",
                                                fontSize: "1rem",
                                                backgroundColor: color.panel,
                                                color: color.fg,
                                                border: `2px solid ${color.border}`,
                                                borderRadius: 6,
                                                outline: "none",
                                                width: "100%"
                                            }}
                                        />
                                    </div>

                                {/* Products/Pallets Table */}
                                {isLoading ? (
                                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                                        <div>Loading products...</div>
                                    </div>
                                ) : selectedProductForPallets ? (
                                    // Show pallets for selected product
                                    <div>
                                        {/* Back button and product info */}
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            marginBottom: '1rem',
                                            padding: '1rem',
                                            backgroundColor: color.panel,
                                            border: `1px solid ${color.border}`,
                                            borderRadius: 6
                                        }}>
                                            <div>
                                                <h4 style={{ margin: 0, color: color.fg }}>
                                                    {selectedProductForPallets.product_name} ({selectedProductForPallets.product_code})
                                                </h4>
                                                <p style={{ margin: '0.25rem 0 0 0', opacity: 0.8, fontSize: '0.9rem' }}>
                                                    Select pallets to add to your order
                                                </p>
                                            </div>
                                            <button
                                                onClick={backToProductSelection}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    backgroundColor: color.primary,
                                                    color: color.bg,
                                                    border: `1px solid ${color.border}`,
                                                    borderRadius: 4,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                ← Back to Products
                                            </button>
                                        </div>

                                        {/* Pallet Filter */}
                                        {productPallets.length > 0 && (
                                            <div style={{ marginBottom: '1rem' }}>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    marginBottom: '0.5rem'
                                                }}>
                                                    <input
                                                        type="text"
                                                        placeholder="Filter pallets by code, lane, type, or status..."
                                                        value={palletFilter}
                                                        onChange={(e) => setPalletFilter(e.target.value)}
                                                        style={{
                                                            flex: 1,
                                                            marginRight: '0.5rem',
                                                            padding: '0.5rem',
                                                            border: `1px solid ${color.border}`,
                                                            borderRadius: 4,
                                                            backgroundColor: color.panel,
                                                            color: color.fg,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    />
                                                    <span style={{ 
                                                        color: color.fg, 
                                                        fontSize: '0.8rem',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {filteredPallets.length} of {productPallets.length} pallets
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Pallets Table */}
                                        {isLoadingPallets ? (
                                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                                <div>Loading pallet locations...</div>
                                            </div>
                                        ) : filteredPallets.length > 0 ? (
                                            <div style={{ 
                                                maxHeight: '400px', 
                                                overflowY: 'auto',
                                                border: `1px solid ${color.border}`,
                                                borderRadius: 6
                                            }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr style={{
                                                        backgroundColor: color.headerBg,
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 1
                                                    }}>
                                                        {[
                                                            { label: "Select", align: "center", width: "10%" },
                                                            { label: "Pallet Code", align: "left", width: "25%" },
                                                            { label: "Lane", align: "left", width: "20%" },
                                                            { label: "Available Stock", align: "center", width: "20%" },
                                                            { label: "Order Quantity", align: "center", width: "25%" },
                                                        ].map(({ label, align, width }) => (
                                                            <th
                                                                key={label}
                                                                style={{
                                                                    textAlign: align,
                                                                    width,
                                                                    padding: ".5rem .75rem",
                                                                    borderBottom: `1px solid ${color.border}`,
                                                                    color: color.fg,
                                                                }}
                                                            >
                                                                {label}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {filteredPallets.map((pallet, index) => {
                                                        const isSelected = isProductSelected(
                                                            pallet.product_id,
                                                            pallet.pallet_id
                                                        );

                                                        const selectedItem = selectedProducts.find(p =>
                                                            p.product_id === pallet.product_id &&
                                                            p.pallet_id === pallet.pallet_id
                                                        );

                                                        // Calculate total quantity already reserved against this pallet
                                                        const reservedQuantity = pallet.ordered_quantity?.reduce(
                                                            (total, order) => total + (order.quantity || 0),
                                                            0
                                                        ) || 0;

                                                        // Stock available for this order
                                                        const availableStock = Math.max(
                                                            0,
                                                            (pallet.stock || 0) - reservedQuantity
                                                        );

                                                        return (
                                                            <tr
                                                                key={index}
                                                                style={{
                                                                    backgroundColor: isSelected
                                                                        ? `${color.success}20`
                                                                        : index % 2 === 0
                                                                            ? color.panel
                                                                            : 'transparent',
                                                                    borderBottom: `1px solid ${color.border}`,
                                                                    opacity: availableStock <= 0 ? 0.5 : 1
                                                                }}
                                                            >
                                                                <td style={{
                                                                    textAlign: "center",
                                                                    padding: ".5rem .75rem",
                                                                    width: "10%"
                                                                }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        disabled={availableStock <= 0}
                                                                        onChange={(e) => {
                                                                            // Extra safety check
                                                                            if (availableStock <= 0) return;

                                                                            if (e.target.checked) {
                                                                                // Default to all available stock for standard pallets
                                                                                let defaultQuantity = 1;

                                                                                if (pallet.pallet_type === 'standard') {
                                                                                    defaultQuantity = availableStock;
                                                                                }

                                                                                setSelectedProducts(prev => [
                                                                                    ...prev,
                                                                                    {
                                                                                        product_id: pallet.product_id,
                                                                                        pallet_id: pallet.pallet_id,
                                                                                        quantity: defaultQuantity,
                                                                                        max_quantity: availableStock,
                                                                                        product_name: pallet.product_name,
                                                                                        product_code: pallet.product_code,
                                                                                        batch_no: pallet.batch_no,
                                                                                        pallet_code: pallet.pallet_code
                                                                                    }
                                                                                ]);
                                                                            } else {
                                                                                // Remove from selection
                                                                                removeSelectedProduct(
                                                                                    pallet.product_id,
                                                                                    pallet.pallet_id
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                </td>

                                                                <td style={{
                                                                    textAlign: "left",
                                                                    padding: ".5rem .75rem",
                                                                    width: "25%",
                                                                    color: color.fg
                                                                }}>
                                                                    {pallet.pallet_code}
                                                                </td>

                                                                <td style={{
                                                                    textAlign: "left",
                                                                    padding: ".5rem .75rem",
                                                                    width: "20%",
                                                                    color: color.fg
                                                                }}>
                                                                    Lane {pallet.lane_id}
                                                                </td>

                                                                <td style={{
                                                                    textAlign: "center",
                                                                    padding: ".5rem .75rem",
                                                                    width: "20%",
                                                                    color: color.fg
                                                                }}>
                                                                    {availableStock.toLocaleString("en-GB")}
                                                                </td>

                                                                <td style={{
                                                                    textAlign: "center",
                                                                    padding: ".5rem .75rem",
                                                                    width: "25%"
                                                                }}>
                                                                    {isSelected && (
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            max={availableStock}
                                                                            value={selectedItem?.quantity || 1}
                                                                            onChange={(e) =>
                                                                                updateSelectedQuantity(
                                                                                    pallet.product_id,
                                                                                    pallet.pallet_id,
                                                                                    parseInt(e.target.value)
                                                                                )
                                                                            }
                                                                            style={{
                                                                                width: "80px",
                                                                                padding: "0.25rem",
                                                                                textAlign: "center",
                                                                                backgroundColor: color.panel,
                                                                                color: color.fg,
                                                                                border: `1px solid ${color.border}`,
                                                                                borderRadius: 3
                                                                            }}
                                                                        />
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                                <div style={{ color: color.fg }}>
                                                    {productPallets.length === 0 
                                                        ? 'No pallets found for this product'
                                                        : 'No pallets match your filter criteria'
                                                    }
                                                </div>
                                                {productPallets.length > 0 && filteredPallets.length === 0 && (
                                                    <button
                                                        onClick={() => setPalletFilter('')}
                                                        style={{
                                                            marginTop: '0.5rem',
                                                            padding: '0.25rem 0.5rem',
                                                            background: color.primary,
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: 4,
                                                            cursor: 'pointer',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        Clear Filter
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : filteredProducts.length > 0 ? (
                                    // Show products list (first step)
                                    <div style={{ 
                                        maxHeight: '400px', 
                                        overflowY: 'auto',
                                        border: `1px solid ${color.border}`,
                                        borderRadius: 6
                                    }}>
                                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                            <thead>
                                                <tr style={{ 
                                                    backgroundColor: color.headerBg,
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 1
                                                }}>
                                                    {[
                                                        { label: "Product Name", align: "left", width: "25%" },
                                                        { label: "Product Code", align: "left", width: "15%" },
                                                        { label: "Batch No", align: "left", width: "15%" },
                                                        { label: "Total Stock", align: "center", width: "12%" },
                                                        { label: "Total Pallets", align: "center", width: "13%" },
                                                        { label: "Action", align: "center", width: "20%" },
                                                    ].map(({ label, align, width }) => (
                                                        <th
                                                            key={label}
                                                            style={{
                                                                textAlign: align,
                                                                width,
                                                                padding: ".5rem .75rem",
                                                                borderBottom: `1px solid ${color.border}`,
                                                                color: color.fg,
                                                            }}
                                                        >
                                                            {label}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredProducts.map((product, index) => (
                                                    <tr
                                                        key={index}
                                                        style={{
                                                            backgroundColor: index % 2 === 0 ? color.panel : 'transparent',
                                                            borderBottom: `1px solid ${color.border}`
                                                        }}
                                                    >
                                                        <td style={{ textAlign: "left", padding: ".5rem .75rem", color: color.fg }}>
                                                            {product.product_name}
                                                        </td>
                                                        <td style={{ textAlign: "left", padding: ".5rem .75rem", color: color.fg }}>
                                                            {product.product_code}
                                                        </td>
                                                        <td style={{ textAlign: "left", padding: ".5rem .75rem", color: color.fg }}>
                                                            {product.batch_no || 'N/A'}
                                                        </td>
                                                        <td style={{ textAlign: "center", padding: ".5rem .75rem", color: color.fg }}>
                                                            {product.quantity_count?.toLocaleString("en-GB") || 0}
                                                        </td>
                                                        <td style={{ textAlign: "center", padding: ".5rem .75rem", color: color.fg }}>
                                                            {product.pallet_count?.toLocaleString("en-GB") || 0}
                                                        </td>
                                                        <td style={{ textAlign: "center", padding: ".5rem .75rem" }}>
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedProductForPallets(product);
                                                                    loadProductPallets(product);
                                                                }}
                                                                style={{
                                                                    padding: "0.5rem 1rem",
                                                                    backgroundColor: color.primary,
                                                                    color: "#fff",
                                                                    border: "none",
                                                                    borderRadius: 4,
                                                                    cursor: "pointer",
                                                                    fontSize: "0.9rem"
                                                                }}
                                                            >
                                                                Select Product
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '2rem',
                                        opacity: 0.7
                                    }}>
                                        {searchTerm ? 'No products found matching your search.' : 'No products available for this customer.'}
                                    </div>
                                )}
                            </div>
                        </section>
                        </>
                    )}

                    {/* Selected Products Summary */}
                    {selectedProducts.length > 0 && (
                        <section
                            style={{
                                marginBottom: "2.5rem",
                                border: `3px solid ${color.border}`,
                                borderRadius: 8,
                                backgroundColor: color.panel,
                            }}
                        >
                            <h3
                                style={{
                                    margin: "0",
                                    padding: "0.75rem 1rem",
                                    borderBottom: `1px solid ${color.border}`,
                                }}
                            >
                                Selected Products ({selectedProducts.length})
                            </h3>
                            
                            <div style={{ padding: "1rem" }}>
                                <div style={{ marginBottom: '1rem' }}>
                                    {selectedProducts.map((item, index) => (
                                        <div key={`${item.product_id}-${item.pallet_id}`} style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '0.75rem',
                                            marginBottom: '0.5rem',
                                            backgroundColor: color.bg,
                                            border: `1px solid ${color.border}`,
                                            borderRadius: 6
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                <strong>{item.product_name}</strong>
                                                <div style={{ fontSize: '0.875rem', color: color.fg, opacity: 0.7 }}>
                                                    Code: <span style={{ fontFamily: 'monospace' }}>{item.product_code}</span>
                                                    {item.batch_no && <span> | Batch: {item.batch_no}</span>}
                                                </div>
                                                <div style={{ fontSize: '0.875rem', color: color.fg, opacity: 0.7 }}>
                                                    Pallet: {item.pallet_code}
                                                </div>
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '0.75rem' 
                                            }}>
                                                <span>Qty: {item.quantity} / {item.max_quantity}</span>
                                                <button
                                                    onClick={() => removeSelectedProduct(item.product_id, item.pallet_id)}
                                                    style={{
                                                        backgroundColor: color.danger,
                                                        color: '#fff',
                                                        border: 'none',
                                                        borderRadius: 3,
                                                        padding: '0.25rem 0.5rem',
                                                        cursor: 'pointer',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Order Notes */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ 
                                        display: 'block', 
                                        marginBottom: '0.5rem',
                                        fontWeight: '500'
                                    }}>
                                        Order Notes (Optional):
                                    </label>
                                    <textarea
                                        value={orderNotes}
                                        onChange={(e) => setOrderNotes(e.target.value)}
                                        placeholder="Add any special instructions or notes for this order..."
                                        style={{
                                            width: '100%',
                                            height: '80px',
                                            padding: '0.5rem 0.75rem',
                                            border: `2px solid ${color.border}`,
                                            borderRadius: 6,
                                            backgroundColor: color.panel,
                                            color: color.fg,
                                            resize: 'vertical',
                                            outline: 'none',
                                            fontSize: '1rem'
                                        }}
                                    />
                                </div>

                                {/* Delivery Information */}
                                <div style={{ marginBottom: '1rem' }}>
                                    <h5 style={{ marginBottom: '0.75rem', color: color.fg }}>
                                        Delivery Information
                                    </h5>
                                    
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '0.75rem',
                                        marginBottom: '0.75rem'
                                    }}>
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}>
                                                Delivery Name: <span style={{ color: color.danger }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={deliveryInfo.delivery_company}
                                                onChange={(e) => handleDeliveryInfoChange('delivery_company', e.target.value)}
                                                placeholder="e.g. Company Name, Person Name..."
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem 0.75rem',
                                                    border: `2px solid ${!deliveryInfo.delivery_company.trim() ? color.danger : color.border}`,
                                                    borderRadius: 6,
                                                    backgroundColor: color.panel,
                                                    color: color.fg,
                                                    outline: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                        </div>
                                        
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}>
                                                Delivery Date: <span style={{ color: color.danger }}>*</span>
                                            </label>
                                            <input
                                                type="date"
                                                value={deliveryInfo.delivery_date}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={(e) => handleDeliveryInfoChange('delivery_date', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem 0.75rem',
                                                    border: `2px solid ${!deliveryInfo.delivery_date ? color.danger : color.border}`,
                                                    borderRadius: 6,
                                                    backgroundColor: color.panel,
                                                    color: color.fg,
                                                    outline: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                        </div>
                                        
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}>
                                                Delivery Postcode: <span style={{ color: color.danger }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={deliveryInfo.delivery_postcode}
                                                onChange={(e) => handleDeliveryInfoChange('delivery_postcode', e.target.value)}
                                                placeholder="e.g. SW1A 1AA"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem 0.75rem',
                                                    border: `2px solid ${!deliveryInfo.delivery_postcode.trim() ? color.danger : 
                                                        !isValidPostcode(deliveryInfo.delivery_postcode) ? color.warning : color.border}`,
                                                    borderRadius: 6,
                                                    backgroundColor: color.panel,
                                                    color: color.fg,
                                                    outline: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                            {deliveryInfo.delivery_postcode.trim() && !isValidPostcode(deliveryInfo.delivery_postcode) && (
                                                <div style={{
                                                    fontSize: '0.75rem',
                                                    color: color.warning,
                                                    marginTop: '0.25rem'
                                                }}>
                                                    Please enter a valid UK postcode format
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}>
                                                Delivery Time (Optional):
                                            </label>
                                            <input
                                                type="time"
                                                value={deliveryInfo.delivery_time}
                                                onChange={(e) => handleDeliveryInfoChange('delivery_time', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem 0.75rem',
                                                    border: `2px solid ${color.border}`,
                                                    borderRadius: 6,
                                                    backgroundColor: color.panel,
                                                    color: color.fg,
                                                    outline: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <label style={{ 
                                            display: 'block', 
                                            marginBottom: '0.25rem',
                                            fontSize: '0.875rem',
                                            fontWeight: '500'
                                        }}>
                                            Delivery Address: <span style={{ color: color.danger }}>*</span>
                                        </label>
                                        <textarea
                                            value={deliveryInfo.delivery_address}
                                            onChange={(e) => handleDeliveryInfoChange('delivery_address', e.target.value)}
                                            placeholder="Full delivery address including street, city, etc."
                                            rows={3}
                                            style={{
                                                width: '100%',
                                                padding: '0.5rem 0.75rem',
                                                border: `2px solid ${!deliveryInfo.delivery_address.trim() ? color.danger : color.border}`,
                                                borderRadius: 6,
                                                backgroundColor: color.panel,
                                                color: color.fg,
                                                resize: 'vertical',
                                                outline: 'none',
                                                fontSize: '0.875rem'
                                            }}
                                        />
                                    </div>
                                    
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: '0.75rem'
                                    }}>
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}>
                                                Collection Date (Optional):
                                            </label>
                                            <input
                                                type="date"
                                                value={deliveryInfo.collection_date}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={(e) => handleDeliveryInfoChange('collection_date', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem 0.75rem',
                                                    border: `2px solid ${color.border}`,
                                                    borderRadius: 6,
                                                    backgroundColor: color.panel,
                                                    color: color.fg,
                                                    outline: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                        </div>
                                        
                                        <div>
                                            <label style={{ 
                                                display: 'block', 
                                                marginBottom: '0.25rem',
                                                fontSize: '0.875rem',
                                                fontWeight: '500'
                                            }}>
                                                Collection Courier (Optional):
                                            </label>
                                            <input
                                                type="text"
                                                value={deliveryInfo.collection_courier}
                                                onChange={(e) => handleDeliveryInfoChange('collection_courier', e.target.value)}
                                                placeholder="Courier company name"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.5rem 0.75rem',
                                                    border: `2px solid ${color.border}`,
                                                    borderRadius: 6,
                                                    backgroundColor: color.panel,
                                                    color: color.fg,
                                                    outline: 'none',
                                                    fontSize: '0.875rem'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Place Order Button */}
                                <button
                                    onClick={placeOrder}
                                    disabled={isLoading}
                                    style={{
                                        backgroundColor: color.success,
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: 6,
                                        padding: '0.75rem 1.5rem',
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        fontSize: '1rem',
                                        fontWeight: '500',
                                        opacity: isLoading ? 0.7 : 1
                                    }}
                                >
                                    {isLoading ? 'Creating Order...' : 'Place Order'}
                                </button>
                            </div>
                        </section>
                    )}
                </div>
            )}

            {/* View Orders Tab */}
            {activeTab === 'view-orders' && (
                <section
                    style={{
                        marginBottom: "2.5rem",
                        border: `3px solid ${color.border}`,
                        borderRadius: 8,
                        backgroundColor: color.panel,
                    }}
                >
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem 1rem',
                        borderBottom: `1px solid ${color.border}`,
                    }}>
                        <h3 style={{ margin: 0 }}>Orders</h3>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '0.875rem',
                            color: realtimeStatus === 'subscribed' ? color.success
                                : realtimeStatus === 'channel_error' ? color.danger
                                : realtimeStatus === 'unavailable' ? color.border
                                : color.warning
                        }}>
                            <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: realtimeStatus === 'subscribed' ? color.success
                                    : realtimeStatus === 'channel_error' ? color.danger
                                    : realtimeStatus === 'unavailable' ? color.border
                                    : color.warning,
                                marginRight: '6px',
                                animation: realtimeStatus === 'subscribed' || realtimeStatus === 'unavailable'
                                    ? 'none'
                                    : 'pulse 1.5s infinite'
                            }} />
                            {realtimeStatus === 'subscribed' ? 'Live Updates'
                                : realtimeStatus === 'channel_error' ? 'Connection Error'
                                : realtimeStatus === 'unavailable' ? 'Offline Mode'
                                : 'Connecting...'}
                        </div>
                    </div>
                    
                    <div style={{ padding: "1rem" }}>
                        {isLoading ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                                <div>Loading orders...</div>
                            </div>
                        ) : orders.length > 0 ? (
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                    <tr style={{ backgroundColor: color.headerBg }}>
                                        {[
                                            { label: "Order #", align: "left", width: "15%" },
                                            { label: "Customer", align: "left", width: "20%" },
                                            { label: "Items", align: "center", width: "10%" },
                                            { label: "Status", align: "center", width: "15%" },
                                            { label: "Created", align: "left", width: "20%" },
                                            { label: "Notes", align: "left", width: "20%" },
                                        ].map(({ label, align, width }) => (
                                            <th
                                                key={label}
                                                style={{
                                                    textAlign: align,
                                                    width,
                                                    padding: ".5rem .75rem",
                                                    borderBottom: `1px solid ${color.border}`,
                                                    color: color.fg,
                                                }}
                                            >
                                                {label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map(order => (
                                        <tr key={order.id}>
                                            <Cell>
                                                <button
                                                    onClick={() => openOrderDetails(order.id)}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        color: color.primary,
                                                        cursor: 'pointer',
                                                        fontFamily: 'monospace',
                                                        fontWeight: '500',
                                                        fontSize: '1rem',
                                                        textDecoration: 'underline',
                                                        padding: 0
                                                    }}
                                                >
                                                    {order.order_number}
                                                </button>
                                            </Cell>
                                            <Cell>{customerName || getCustomerName(order.customer_id)}</Cell>
                                            <Cell center>{order.total_items}</Cell>
                                            <Cell center>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '0.25rem 0.5rem',
                                                    borderRadius: '12px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '500',
                                                    width: '100px',
                                                    textAlign: 'center',
                                                    backgroundColor: 
                                                        (order.status === 'pending' && !order.is_picking) ? '#d39b00ff' :
                                                        order.status === 'picking' ? color.primary :
                                                        order.status === 'picked' ? color.success :
                                                        order.status === 'dispatched' ? '#2f90ffff' :
                                                        (order.status === 'pending' && order.is_picking) ? 'rgb(185, 7, 255)' :
                                                        color.danger + '40',
                                                    color: 
                                                        order.status === 'pending' ? '#ffffff' :
                                                        order.status === 'picking' ? '#ffffff' :
                                                        order.status === 'picked' ? '#ffffff' :
                                                        order.status === 'dispatched' ? '#ffffff' :
                                                        color.danger
                                                }}>
                                                    {order.is_picking && order.status === 'pending' ? 'Picking...' :
                                                     order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                                </span>
                                            </Cell>
                                            <Cell>{formatDate(order.created_at)}</Cell>
                                            <Cell>{order.notes || '-'}</Cell>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ 
                                textAlign: 'center', 
                                padding: '2rem',
                                opacity: 0.7
                            }}>
                                No orders found.
                            </div>
                        )}
                    </div>
                </section>
            )}
            
            {/* Order Details Modal */}
            {orderDetailsOpen && (
                <OrderDetailsModal
                    orderId={selectedOrderId}
                    realtimeOrder={realtimeOrder}
                    onClose={closeOrderDetails}
                    onOrderUpdate={handleOrderUpdate}
                    color={color}
                    theme={theme}
                    profile={profile}
                    isAdmin={isAdmin}
                    isWarehouse={isWarehouse}
                    isCustomer={isCustomer}
                    warehouseData={warehouseData}
                />
            )}
        </div>
    );

    function Cell({ children, num = false, center = false }) {
        return (
            <td
                style={{
                    padding: ".5rem .75rem",
                    borderBottom: `1px solid ${color.border}`,
                    textAlign: center ? "center" : num ? "right" : "left",
                }}
            >
                {children}
            </td>
        );
    }
}
